import { Star } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import React from "react";
import { FormattedNumber } from "react-intl";
import { InventoryValueEntry, useGetV4WarehousesQuery, Warehouse, Requisition as ApiRequisition } from 'randmar-api-client';
import { Requisition as LegacyRequisition } from "../../stores/document/Document";
import CurrencyUtils from "../util/CurrencyUtils";
import LoadingComponent from '../util/LoadingComponent';

interface IProps {
  inventoryValues: InventoryValueEntry[];
  requisitions: (LegacyRequisition | ApiRequisition)[];
}

const isLegacy = (r: LegacyRequisition | ApiRequisition): r is LegacyRequisition => !!r && 'location' in r;

const VendorValues: React.FC<IProps> = ({ inventoryValues, requisitions: rawRequisitions }) => {
  const { data: warehousesData, isLoading } = useGetV4WarehousesQuery();
  const warehouses = (warehousesData as Warehouse[]) ?? [];

  const requisitions = React.useMemo(() => rawRequisitions.map(r =>
    isLegacy(r)
      ? {
        Location: r.location,
        SubTotal: r.subTotal
      }
      : r
  ), [rawRequisitions]);

  const warehouseDisplayData = React.useMemo(() => warehouses.map(w => {
    const inventoryForWarehouse = inventoryValues.filter(iv => iv.Location === w.WarehouseId);
    const requisitionsForWarehouse = requisitions.filter(po => po.Location === w.WarehouseId);

    const inventoryValue = inventoryForWarehouse.reduce((sum, value) => sum + (value.InventoryValue || 0), 0);
    const purchaseOrderValue = inventoryForWarehouse.reduce((sum, value) => sum + (value.PurchaseOrderValue || 0), 0);
    const salesOrderValue = inventoryForWarehouse.reduce((sum, value) => sum + (value.SalesOrderValue || 0), 0);
    const draftOrdersValue = requisitionsForWarehouse.reduce((sum, value) => sum + (value.SubTotal || 0), 0);

    const shouldDisplay = (inventoryValue + purchaseOrderValue + salesOrderValue > 0) || (draftOrdersValue > 0) || ['MTL', 'EDM'].includes(w.WarehouseId || "");
    const isPrimary = ['MTL', 'EDM'].includes(w.WarehouseId || "");

    return {
      warehouse: w,
      inventoryValue,
      draftOrdersValue,
      purchaseOrderValue,
      salesOrderValue,
      shouldDisplay,
      isPrimary
    };
  }).filter(d => d.shouldDisplay), [warehouses, inventoryValues, requisitions]);

  const backOrderLocations = ['GHOST', '3PLW', '3PLE'];
  const backOrdersData = React.useMemo(() => {
    const inventoryForBackOrders = inventoryValues.filter(iv => backOrderLocations.includes(iv.Location || ""));
    const requisitionsForBackOrders = requisitions.filter(r => backOrderLocations.includes(r.Location || ""));

    const inventoryValue = inventoryForBackOrders.reduce((sum, value) => sum + (value.InventoryValue || 0), 0);
    const purchaseOrderValue = inventoryForBackOrders.reduce((sum, value) => sum + (value.PurchaseOrderValue || 0), 0);
    const salesOrderValue = inventoryForBackOrders.reduce((sum, value) => sum + (value.SalesOrderValue || 0), 0);
    const draftOrdersValue = requisitionsForBackOrders.reduce((sum, value) => sum + (value.SubTotal || 0), 0);

    const shouldDisplay = (inventoryValue + purchaseOrderValue + salesOrderValue > 0) || (draftOrdersValue > 0);

    return {
      inventoryValue,
      draftOrdersValue,
      purchaseOrderValue,
      salesOrderValue,
      shouldDisplay
    };
  }, [inventoryValues, requisitions]);

  const totals = React.useMemo(() => ({
    inventory: inventoryValues.reduce((sum, value) => sum + (value.InventoryValue || 0), 0),
    draftOrders: requisitions.reduce((sum, value) => sum + (value.SubTotal || 0), 0),
    purchaseOrders: inventoryValues.reduce((sum, value) => sum + (value.PurchaseOrderValue || 0), 0),
    salesOrders: inventoryValues.reduce((sum, value) => sum + (value.SalesOrderValue || 0), 0)
  }), [inventoryValues, requisitions]);


  if (isLoading) return <LoadingComponent text="Loading warehouses..." />;

  return (
    <div className="w-full overflow-auto rounded-[20px]">
      <Table size="small">
        <TableHead>
          <TableRow className="bg-[#f9f5ff]">
            <TableCell align="center" className="border-b border-[#582c83]" />
            <TableCell align="center" className="border-b border-[#582c83]" title="Randmar Warehouse">Location</TableCell>
            <TableCell align="center" className="border-b border-[#582c83]" title="Inventory Value">Inventory Value</TableCell>
            <TableCell align="center" className="border-b border-[#582c83]" title="Amount on Draft Order">Draft Orders</TableCell>
            <TableCell align="center" className="border-b border-[#582c83]" title="Amount on Purchase Order">POs</TableCell>
            <TableCell align="center" className="border-b border-[#582c83]" title="Amount on Sales Order">SOs</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {warehouseDisplayData.map(data => (
            <TableRow key={data.warehouse.WarehouseId} className="hover:bg-[#f9f5ff]" >
              <TableCell>
                {data.isPrimary && <Star className="h-5 w-5 text-[#582c83] fill-[#582c83]" />}
              </TableCell>
              <TableCell> {data.isPrimary ? <b>{data.warehouse.Name}</b> : <>{data.warehouse.Name}</>}</TableCell>
              <TableCell>
                <FormattedNumber value={data.inventoryValue} {...CurrencyUtils.format} />
              </TableCell>
              <TableCell>
                <FormattedNumber value={data.draftOrdersValue} {...CurrencyUtils.format} />
              </TableCell>
              <TableCell>
                <FormattedNumber value={data.purchaseOrderValue} {...CurrencyUtils.format} />
              </TableCell>
              <TableCell>
                <FormattedNumber value={data.salesOrderValue} {...CurrencyUtils.format} />
              </TableCell>
            </TableRow>
          ))}
          {backOrdersData.shouldDisplay &&
            <TableRow key="otherWarehouses" className="hover:bg-[#f9f5ff]">
              <TableCell />
              <TableCell>Back Orders</TableCell>
              <TableCell>
                <FormattedNumber value={backOrdersData.inventoryValue} {...CurrencyUtils.format} />
              </TableCell>
              <TableCell>
                <FormattedNumber value={backOrdersData.draftOrdersValue} {...CurrencyUtils.format} />
              </TableCell>
              <TableCell>
                <FormattedNumber value={backOrdersData.purchaseOrderValue} {...CurrencyUtils.format} />
              </TableCell>
              <TableCell>
                <FormattedNumber value={backOrdersData.salesOrderValue} {...CurrencyUtils.format} />
              </TableCell>
            </TableRow>
          }
          <TableRow className="bg-[#f9f5ff] text-2xl">
            <TableCell />
            <TableCell className="text-lg font-bold">Total</TableCell>
            <TableCell className="text-lg font-bold">
              <FormattedNumber value={totals.inventory} {...CurrencyUtils.format} />
            </TableCell>
            <TableCell className="text-lg font-bold">
              <FormattedNumber value={totals.draftOrders} {...CurrencyUtils.format} />
            </TableCell>
            <TableCell className="text-lg font-bold">
              <FormattedNumber value={totals.purchaseOrders} {...CurrencyUtils.format} />
            </TableCell>
            <TableCell className="text-lg font-bold">
              <FormattedNumber value={totals.salesOrders} {...CurrencyUtils.format} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

export default VendorValues;
